import React, { useState, useRef, useEffect } from 'react';
import { MessageCircle, X, Send } from 'lucide-react';
import { Link } from 'react-router-dom';

interface Message {
  id: string;
  text: string;
  sender: 'user' | 'bot';
  timestamp: Date;
  type?: 'text' | 'map' | 'link';
  link?: { text: string; url: string };
}

const ChatBot: React.FC = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: `Hello! Welcome to ShreeGuruCool Technologies. I'm here to help you with any questions about our SAP S4HANA courses. How can I assist you today?`,
      sender: 'bot',
      timestamp: new Date(),
      type: 'text',
    },
  ]);
  const [inputMessage, setInputMessage] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const predefinedResponses: Record<string, Message> = {
    hello: {
      id: Date.now().toString(),
      text: 'Hello! How can I help you with our SAP S4HANA courses today?',
      sender: 'bot',
      timestamp: new Date(),
    },
    hi: {
      id: Date.now().toString(),
      text: 'Hi there! What would you like to know about our coaching programs?',
      sender: 'bot',
      timestamp: new Date(),
    },
    courses: {
      id: Date.now().toString(),
      text: 'We offer SAP S/4HANA courses: Sales & Distribution (SD), Materials Management (MM), and Logistics Execution (LE). Which one interests you?',
      sender: 'bot',
      timestamp: new Date(),
    },
    price: {
      id: Date.now().toString(),
      text: 'For fees details and personalized course plans, please contact a representative.',
      sender: 'bot',
      timestamp: new Date(),
    },
    duration: {
      id: Date.now().toString(),
      text: 'Courses last 2.5 to 3 months. Weekdays (7–9 PM) & Weekends (10 AM–4 PM) options available.',
      sender: 'bot',
      timestamp: new Date(),
    },
    placement: {
      id: Date.now().toString(),
      text: 'We provide placement assistance with resume help, mock interviews, and connections to TCS, Infosys, Wipro, and more.',
      sender: 'bot',
      timestamp: new Date(),
    },
    online: {
      id: Date.now().toString(),
      text: 'Yes! We offer both online (live + recorded) and offline classroom sessions.',
      sender: 'bot',
      timestamp: new Date(),
    },
    certification: {
      id: Date.now().toString(),
      text: 'All courses include certification preparation and certificates upon completion.',
      sender: 'bot',
      timestamp: new Date(),
    },
    contact: {
      id: Date.now().toString(),
      text: 'You can call us at +91 98765 43210 or email info@shreegurucoool.com. WhatsApp also available!',
      sender: 'bot',
      timestamp: new Date(),
    },
    demo: {
      id: Date.now().toString(),
      text: 'We offer FREE demo classes. Would you like to book one now?',
      sender: 'bot',
      timestamp: new Date(),
      type: 'link',
      link: { text: 'Book Demo Class', url: '/demo-class' },
    },
    experience: {
      id: Date.now().toString(),
      text: 'Our instructors have 10+ years of SAP industry experience and certifications.',
      sender: 'bot',
      timestamp: new Date(),
    },
    batch: {
      id: Date.now().toString(),
      text: 'Small batches (max 15 students) to ensure focused learning.',
      sender: 'bot',
      timestamp: new Date(),
    },
    schedule: {
      id: Date.now().toString(),
      text: 'Weekdays (7–9 PM) and Weekends (10 AM–4 PM). Corporate training available.',
      sender: 'bot',
      timestamp: new Date(),
    },
    support: {
      id: Date.now().toString(),
      text: '24/7 student support, lifetime material access, doubt-clearing sessions.',
      sender: 'bot',
      timestamp: new Date(),
    },
  };

  const getBotResponse = (userMessage: string): Message => {
    const message = userMessage.toLowerCase();

    if (message.includes('location') || message.includes('map')) {
      return {
        id: Date.now().toString(),
        text: 'Our location is pinned on the map below:',
        sender: 'bot',
        timestamp: new Date(),
        type: 'map',
      };
    }

    if (predefinedResponses[message]) {
      return predefinedResponses[message];
    }

    for (const key in predefinedResponses) {
      if (message.includes(key)) {
        return predefinedResponses[key];
      }
    }

    return {
      id: Date.now().toString(),
      text: `I'm sorry, I can't answer that question. For further details, please contact us at +91 77950 06081.`,
      sender: 'bot',
      timestamp: new Date(),
    };
  };

  const handleSendMessage = () => {
    if (!inputMessage.trim()) return;

    const userMsg: Message = {
      id: Date.now().toString(),
      text: inputMessage,
      sender: 'user',
      timestamp: new Date(),
      type: 'text',
    };

    setMessages((prev) => [...prev, userMsg]);
    setInputMessage('');
    setIsTyping(true);

    setTimeout(() => {
      const botResponse = getBotResponse(userMsg.text);
      setMessages((prev) => [...prev, botResponse]);
      setIsTyping(false);
    }, 1000);
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };

  const quickActions = [
    { text: 'Course Info', keyword: 'courses' },
    { text: 'Fees & Duration', keyword: 'price' },
    { text: 'Book Demo', keyword: 'demo' },
    { text: 'Show Map', keyword: 'location' },
  ];

  const handleQuickAction = (keyword: string) => {
    setInputMessage(keyword);
    handleSendMessage();
  };

  return (
    <>
      {/* Floating Button */}
      <div className="fixed bottom-6 right-6 z-50">
        <button
          onClick={() => setIsOpen(!isOpen)}
          className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-full shadow-lg hover:shadow-xl transform hover:scale-105 transition-all duration-300 animate-pulse"
        >
          {isOpen ? <X className="h-6 w-6" /> : <MessageCircle className="h-6 w-6" />}
        </button>
      </div>

      {/* Chat Window */}
      {isOpen && (
        <div className="fixed bottom-24 right-6 w-[calc(100%-3rem)] sm:w-96 h-[calc(100%-10rem)] md:h-[500px] bg-white rounded-lg shadow-2xl border border-gray-200 z-50 flex flex-col">
          {/* Header */}
          <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-4 rounded-t-lg flex items-center justify-between">
            <div className="flex items-center gap-3">
              <img src="/logo.jpeg" alt="Logo" className="h-8 w-8 rounded-full" />
              <div>
                <h3 className="font-semibold">ShreeGuruCool Assistant</h3>
                <p className="text-xs text-blue-100">Online • Ready to help</p>
              </div>
            </div>
            <X className="h-5 w-5 cursor-pointer" onClick={() => setIsOpen(false)} />
          </div>

          {/* Messages */}
          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {messages.map((msg) => (
              <div
                key={msg.id}
                className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}
              >
                {msg.type === 'map' ? (
                  <div className="max-w-[80%] w-full">
                    <p className="text-sm text-gray-700 mb-2">{msg.text}</p>
                    <iframe
                      title="ShreeGuruCool Location"
                      src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d3888.3284193438167!2d77.70232817418791!3d12.950824315335247!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x3bae1235e3ec686b%3A0xb6fef3cde9aeace1!2sShreeGuruCool%20teKnologies!5e0!3m2!1sen!2sin!4v1752652581992!5m2!1sen!2sin"
                      width="100%"
                      height="200"
                      allowFullScreen
                      loading="lazy"
                      style={{ border: 0 }}
                      referrerPolicy="no-referrer-when-downgrade"
                      className="rounded"
                    ></iframe>
                    <p className="text-xs text-gray-500 mt-1">
                      2nd Floor, Above Nest Bakery, CKB Layout, Marathahalli, Bangalore
                    </p>
                  </div>
                ) : (
                  <div
                    className={`max-w-[80%] p-3 rounded-lg ${
                      msg.sender === 'user'
                        ? 'bg-blue-600 text-white rounded-br-none'
                        : 'bg-gray-100 text-gray-800 rounded-bl-none'
                    }`}
                  >
                    <p className="text-sm whitespace-pre-line">{msg.text}</p>
                    {msg.type === 'link' && msg.link && (
                      <Link
                        to={msg.link.url}
                        className="mt-2 inline-block bg-white text-blue-600 font-semibold px-3 py-1 rounded-full text-xs hover:bg-gray-50 transition-colors"
                      >
                        {msg.link.text}
                      </Link>
                    )}
                    <p
                      className={`text-xs mt-1 ${
                        msg.sender === 'user' ? 'text-blue-100' : 'text-gray-500'
                      }`}
                    >
                      {msg.timestamp.toLocaleTimeString([], {
                        hour: '2-digit',
                        minute: '2-digit',
                      })}
                    </p>
                  </div>
                )}
              </div>
            ))}
            {isTyping && (
              <div className="flex justify-start">
                <div className="bg-gray-100 text-gray-800 p-3 rounded-lg rounded-bl-none">
                  <div className="flex space-x-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: '0.1s' }}
                    ></div>
                    <div
                      className="w-2 h-2 bg-gray-400 rounded-full animate-bounce"
                      style={{ animationDelay: '0.2s' }}
                    ></div>
                  </div>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>

          {/* Quick Actions */}
          {messages.length === 1 && (
            <div className="px-4 pb-2">
              <p className="text-xs text-gray-500 mb-2">Quick questions:</p>
              <div className="grid grid-cols-2 gap-2">
                {quickActions.map((qa, idx) => (
                  <button
                    key={idx}
                    onClick={() => handleQuickAction(qa.keyword)}
                    className="text-xs bg-gray-100 hover:bg-gray-200 p-2 rounded border text-gray-700"
                  >
                    {qa.text}
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Input Field */}
          <div className="p-4 border-t border-gray-200">
            <div className="flex gap-2">
              <input
                type="text"
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type your message..."
                className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm"
              />
              <button
                onClick={handleSendMessage}
                disabled={!inputMessage.trim()}
                className="bg-blue-600 text-white p-2 rounded-lg hover:bg-blue-700 disabled:opacity-50"
              >
                <Send className="h-4 w-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default ChatBot;
